import Pin from "./Pin";
// import Form from "react-bootstrap/Form";
import TimeField from "../libs/timeField";
import { getArrivalTimeAsDayjs, getDepartureTimeAsDayjs, getDistanceInMetres, getStartDepotDepartureTime, getEndDepotArrivalTime, formatDateTime } from "../libs/routes-lib";
import dayjs from "../dayjs";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useScroll } from "../libs/hooksLib";
import { Form, Button, Col, Flex, Popover, Popconfirm, Row, Tooltip, Card, Space, DatePicker, TimePicker, Switch, Select, Input, Tag } from "antd";
import { useAppContext } from "../libs/contextLib";
import { noop } from "lodash/util";
import { dayjsToSecsSinceMidnight, secsSinceMidnightToDayjs } from "../model/timeFilter";
import RouteNumber from "./RouteNumber";
import { find } from "lodash/collection";
import { uniq } from "lodash/array";
import { EyeOutlined, DragOutlined, PlusOutlined, EditOutlined, SyncOutlined } from "@ant-design/icons";
import { ReactComponent as Trash } from '../assets/icons/Trash.svg';
import { values } from "lodash/object";
import LoadMessage from './LoadMessage';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import LocationLookup from './LocationLookup';
import { ulid } from "ulid";
import { stopModelData } from "../services/ModelService";
import { ReactComponent as Oneway } from '../assets/icons/Oneway.svg';
import { ReactComponent as Return } from '../assets/icons/Return.svg';
import { ReactComponent as Warning } from '../assets/icons/Warning.svg';
import { ReactComponent as Time } from '../assets/icons/Time.svg';
import { clone, first, last } from "lodash";
import DisplayStop from "./DisplayStop";
import useAllStops from "../hooks/useAllStops";
import { getCachedPathDebounced } from "../libs/pathLib";
import { BusRoute, DATE_STRING } from "../model/busRoute";
import { Bus } from "./Icons";
import useAllVehicleTypes from "../hooks/useAllVehicleType";
import { dA } from "@fullcalendar/core/internal-common";
import {ReactComponent as Edit} from '../assets/icons/Edit.svg';
import {ReactComponent as Check} from "../assets/icons/Check.svg";
import {ReactComponent as Notes2} from '../assets/icons/Notes_2.svg';

// Add CSS for the close button hover effects
const closeButtonStyles = `
.time-field-close-btn {
    transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease !important;
}

.time-field-close-btn:hover {
    background-color: #ff4d4f !important;
    border-color: #ff4d4f !important;
    color: #fff !important;
}
`;

// Inject the styles
if (typeof document !== 'undefined') {
    const styleElement = document.createElement('style');
    styleElement.textContent = closeButtonStyles;
    if (!document.head.querySelector('style[data-time-field-close]')) {
        styleElement.setAttribute('data-time-field-close', 'true');
        document.head.appendChild(styleElement);
    }
}

// ============================================================================
// Module-level Constants
// ============================================================================
const MAX_PLACEHOLDERS = 25;
const MAP_FIT_ANIMATION_MS = 500;
const MAP_FIT_PADDING = 50;
const MAP_FIT_MAX_ZOOM = 15;

// Optional: set to true to disable viewport adjustments during debugging
const DISABLE_MAP_VIEWPORT_UPDATES = false;

// ============================================================================
// Module-level Helper Functions - Stop Classification
// ============================================================================
const getRealStops = (stops) => stops.filter(s => !s.placeholder && !s.isReturnStop);
const getPlaceholders = (stops) => stops.filter(s => s.placeholder);
const getMirrorStop = (stops) => stops.find(s => s.isReturnStop);

// ============================================================================
// Module-level Helper Functions - Neighbour Finding
// ============================================================================
// Critical: When replacing placeholder, we need adjacent real stops for route calculation
const findPreviousRealStop = (stops, fromIdx) => {
    for (let i = fromIdx - 1; i >= 0; i--) {
        if (!stops[i].placeholder && !stops[i].isReturnStop) return stops[i];
    }
    return null;
};

const findNextRealStop = (stops, fromIdx) => {
    for (let i = fromIdx + 1; i < stops.length; i++) {
        if (!stops[i].placeholder && !stops[i].isReturnStop) return stops[i];
    }
    return null;
};

// ============================================================================
// Module-level Helper Functions - Validation
// ============================================================================
const validateRouteForSave = (route) => {
    const realStops = getRealStops(route.stops);
    const placeholders = getPlaceholders(route.stops);
    const firstStop = route.stops[0];
    
    const result = {
        canSave: false,
        warnings: [],
        errors: []
    };
    
    // Minimum stop requirement
    if (realStops.length < 2) {
        result.errors.push('Route must have at least 2 stops');
        return result;
    }
    
    // Return mode specific: first stop cannot be placeholder
    if (route.returnEnabled && firstStop?.placeholder) {
        result.errors.push('Cannot save return trip with empty first stop');
        return result;
    }
    
    // Note placeholders that will be removed (logged, not shown in UI yet)
    if (placeholders.length > 0) {
        result.warnings.push(
            `${placeholders.length} empty stop${placeholders.length > 1 ? 's' : ''} will be removed when you save`
        );
    }
    
    result.canSave = true;
    return result;
};

// ============================================================================
// Module-level Helper Functions - Map Viewport
// ============================================================================
// Factory function to create map fitting helper with setFocusStop bound
const createFitMapToRoute = (setFocusStop) => {
    return (stops, options = {}) => {
        if (DISABLE_MAP_VIEWPORT_UPDATES) return;
        
        const { force = false } = options;
        const realStops = getRealStops(stops);
        
        // Need at least one real stop to adjust viewport (unless forced)
        if (realStops.length === 0 && !force) return;
        
        if (realStops.length > 0) {
            // Trigger parent map to fit bounds around all real stops
            setFocusStop({
                ...realStops[0],
                fitBounds: true,
                stops: realStops
            });
        }
    };
};

const getStableStopId = (stop) => {
    if (!stop) {
        return 'stop-null';
    }
    if (stop.isReturnStop) {
        return `return-${stop.stopTimeId || stop.stopId || 'final'}`;
    }
    return stop.stopTimeId || (stop.stopId ? `stop-${stop.stopId}` : 'stop-unknown');
};

function StopTime({
    route, charter,
    idx,
    trip,
    stopValidationMessages,
    arriveValidationMessages,
    departValidationMessages,
    isAdmin,
    immutable,
    setEditing,
    editing,
    setFocusStop,
    setSelectedStop,
    selectedStop,
    scrollRef,
    executeScroll,
    setUpdated,
    stop,
    previousStop,
    previousStopTime,
    allStops,
    operator,
    showSeconds,
    cumulativeUpdate,
    focus,
    setFocus,
    focusTransfers,
    setFocusTransfers = noop,
    setAllStops,
    handleAddStopTime,
    setRoute,
    setRouteNow,
    stopTimes,
    viewOnly = false,
    calculateArrivalTime,
    isRecalculating = false,
    fitMapToRoute  // Map viewport helper
}) {

    const { setChatContext, setChatOpen, messageApi } = useAppContext();
    const [editingETA, setEditingETA] = useState(false);
    const [showNotes, setShowNotes] = useState(false);
    const [localNotes, setLocalNotes] = useState('');
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({
        id: stop?.stopTimeId,
        data: {
            type: 'stop',
            stop
        }
    });

    // Add keyboard shortcut for editing arrival time
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isEditingArrival) {
                const currentType = getStopType();
                if (currentType === 'both') {
                    handleStopTypeChange('pickup');
                } else if (currentType === 'dropoff') {
                    handleStopTypeChange('pickup');
                }
            }
        };
        
        if (isEditingArrival) {
            document.addEventListener('keydown', handleKeyDown);
            return () => document.removeEventListener('keydown', handleKeyDown);
        }
    }, [stop?.charterStopType]);

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    // Add missing variables
    const isFirst = idx === 0;
    const isLast = idx === stopTimes.length - 1;
    const returnEnabled = route?.returnEnabled && stopTimes.filter(s => s.stopId).length > 1;
    const index = idx + 1; // 1-based index for display

    const setTrip = useCallback((trip, now) => {
        route = route.clone();
        const tripIdx = route.services.findIndex(t => t.tripId === trip.tripId);
        route.services[tripIdx] = trip;
        route = new BusRoute(route);
        route.updateStops();

        if(now) {
            setRouteNow(route);
        } else {
            setRoute(route);
        }
    }, [setRoute, setRouteNow, allStops, route]);

    // Add back the onChange function
    const onChange = (changes) => {
        if(changes.date) {
            if(idx === 0) trip.date = changes.date;
            else trip.dates[idx] = changes.date;
            setTrip(trip, true);
            setUpdated(true);
        } else {
            const newStopTimes = [...stopTimes];

            const stopTimeChanges = {
                arriveSecs: changes.arriveSecs,
                departSecs: changes.departSecs,
                stopId: changes.stopId,
                stopTimeId: changes.stopTimeId,
                charterStopType: changes.charterStopType,
                notes: changes.notes,
            }

            // Update the stop in the trip's stopTimes array
            newStopTimes[idx] = {
                ...newStopTimes[idx],
                ...stopTimeChanges
            };
            trip.stopTimes = newStopTimes;
            setTrip(trip, true);
            setUpdated(true);
        }
    };

    const onDelete = useCallback(async () => {
        // Prevent deleting mirror stop
        if (stop.isReturnStop) {
            messageApi.warning('Cannot delete return stop. Disable return mode first.');
            return;
        }
        
        // Enforce minimum real stops
        const realStops = getRealStops(route.stops);
        if (realStops.length < 2) {
            messageApi.warning('Route must have at least 2 stops.');
            return;
        }
        
        const isFirstStop = idx === 0;
        const wasRealStop = !stop.placeholder;
        
        const removeIdx = route.stops.findIndex(s => s.stopTimeId === stop.stopTimeId);
        if (removeIdx === -1) {
            console.warn('[SimpleStopTimes] onDelete could not find stop to remove', stop.stopTimeId);
            return;
        }

        const removeStopAtIndex = (arr, index) => arr.filter((_, i) => i !== index);
        route.stops = removeStopAtIndex(route.stops, removeIdx);
        route.stopTimes = removeStopAtIndex(route.stopTimes, removeIdx);
        route.services.forEach(service => {
            if (service.stops) {
                service.stops = removeStopAtIndex(service.stops, removeIdx);
            }
            if (service.stopTimes) {
                service.stopTimes = removeStopAtIndex(service.stopTimes, removeIdx);
            }
        });
        
        // Handle mirror stop update if first stop deleted in return mode
        if (route.returnEnabled && isFirstStop && route.stops.length > 0) {
            const newFirstStop = route.stops[0];
            const mirror = getMirrorStop(route.stops);
            
            if (mirror) {
                if (!newFirstStop.placeholder) {
                    // Update mirror to match new first stop
                    mirror.lat = newFirstStop.lat;
                    mirror.lon = newFirstStop.lon;
                    mirror.stopId = newFirstStop.stopId;
                    mirror.stopName = newFirstStop.stopName;
                    mirror.address = newFirstStop.address;
                    delete mirror.placeholder;
                    // CRITICAL: Preserve return stop flags
                    mirror.isReturnStop = true;
                    mirror.immutable = true;
                } else {
                    // Mark mirror as pending
                    mirror.placeholder = true;
                    mirror.lat = null;
                    mirror.lon = null;
                    mirror.stopId = null;
                    mirror.stopName = '';
                    // CRITICAL: Preserve return stop flags even when placeholder
                    mirror.isReturnStop = true;
                    mirror.immutable = true;
                }
            }
        }
        
        // Recalculate route (only for real stops)
        if (wasRealStop && route.stops.length >= 2) {
            await route.rebuildWaypoints();

            route.calculateStartEnd({
                stops: route.stops,
                firstStop: route.stops[0],
                lastStop: route.stops[route.stops.length - 1],
                trim: true
            });
            
            // Update map viewport to fit remaining stops
            if (fitMapToRoute) {
                fitMapToRoute(route.stops, { force: true });
            }
        }
        
        setRouteNow(route.clone());
        setUpdated(true);
    }, [route, idx, stop, setRouteNow, setUpdated, messageApi, fitMapToRoute]);

    const handleClick = (e) => {
        // Only handle click if not dragging
        if (!transform) {
            e.stopPropagation();
            const stopWithDetails = { ...stop, ...allStops[stop?.stopId] };
            setSelectedStop(stopWithDetails);
            if (executeScroll) {
                executeScroll(stopWithDetails);
            }
        }
    };

    const getStopType = () => {
        return stop?.charterStopType || (isFirst ? 'pickup' : 'dropoff');
    };

    const { isEditingArrival, isEditingDeparture } = useMemo(() => {
        const stopType = getStopType();
        return {
            isEditingArrival: stopType === 'dropoff' || stopType === 'both',
            isEditingDeparture: stopType === 'pickup' || stopType === 'both'
        };
    }, [getStopType]);


    // Initialize local notes from stop.notes
    useEffect(() => {
        setLocalNotes(stop?.notes || '');
    }, [stop?.notes]);

    // Show notes if there's existing text
    useEffect(() => {
        if (stop?.notes && stop.notes.trim().length > 0) {
            setShowNotes(true);
        }
    }, [stop?.notes]);

    const getStopTypeLabel = () => {
        const stopType = getStopType();
        if (stopType === 'both') return 'Drop-off & Pick-up';
        return stopType === 'pickup' ? 'Pick-up' : 'Drop-off';
    };

    const handleNotesChange = (value) => {
        setLocalNotes(value);
    };

    const persistNotes = () => {
        const updatedStopTimes = [...stopTimes];
        const currentStop = updatedStopTimes[idx];
        currentStop.notes = localNotes;
        trip.stopTimes = updatedStopTimes;
        setTrip(trip, true);
        setUpdated(true);
    };

    const handleStopTypeChange = (newStopType) => {
        if (!stop) return;
        
        // First stop should always be pickup only, last stop should always be dropoff only
        if (isFirst && newStopType !== 'pickup') {
            newStopType = 'pickup';
        } else if (isLast && newStopType !== 'dropoff') {
            newStopType = 'dropoff';
        }
        
        // When switching stop types, we need to ensure the right time fields are set
        const currentStopType = getStopType();
        const updatedStopTimes = [...stopTimes];
        const currentStop = updatedStopTimes[idx];
        currentStop.charterStopType = newStopType;
        
        // If switching from pickup to dropoff, copy depart time to arrive time
        if (currentStopType === 'pickup' && newStopType === 'dropoff') {
            currentStop.arriveSecs = currentStop.departSecs;
        }
        // If switching from dropoff to pickup, copy arrive time to depart time
        else if (currentStopType === 'dropoff' && newStopType === 'pickup') {
            currentStop.departSecs = currentStop.arriveSecs;
        }
        // If switching to 'both', ensure both times are set
        else if (newStopType === 'both') {
            // If we only had one time set, copy it to the other
            if (currentStop.arriveSecs === 0 || currentStop.arriveSecs === currentStop.departSecs) {
                currentStop.arriveSecs = currentStop.departSecs;
            }
            if (currentStop.departSecs === 0 || currentStop.departSecs === currentStop.arriveSecs) {
                currentStop.departSecs = currentStop.arriveSecs;
            }
        }
        
        
        trip.stopTimes = updatedStopTimes;
        setTrip(trip, true);
        setUpdated(true);
    };

    const handleDepartTimeChange = (e, time) => {
        if (!time) return;
        const value = dayjs(time, 'HH:mm');
        if (!value.isValid()) return;

        // Get the current stop's departure time relative to the trip's date
        const currentDepartTime = trip.getDepartureTimeAsDayjs(stop);
        const currentRelativeDepartSecs = dayjsToSecsSinceMidnight(currentDepartTime);
        
        // Convert the input time to seconds since midnight
        const newRelativeDepartSecs = dayjsToSecsSinceMidnight(value);
        
        // Calculate the time difference in seconds
        const timeDiff = newRelativeDepartSecs - currentRelativeDepartSecs;
        
        // Create a changes object with the new departure time
        const changes = {
            arriveSecs: idx === 0 ? stop.arriveSecs + timeDiff : stop.arriveSecs,
            departSecs: stop.departSecs + timeDiff
        };

        // If cumulative update is enabled, update subsequent stops
        if (cumulativeUpdate) {
            const updatedStopTimes = [...stopTimes];

            // Update the current stop's times
            updatedStopTimes[idx] = {
                ...updatedStopTimes[idx],
                ...changes
            };

            // Update subsequent stops by adding the time difference
            for (let i = idx + 1; i < updatedStopTimes.length; i++) {
                updatedStopTimes[i] = {
                    ...updatedStopTimes[i],
                    arriveSecs: updatedStopTimes[i].arriveSecs + timeDiff,
                    departSecs: updatedStopTimes[i].departSecs + timeDiff
                };
            }

            trip.stopTimes = updatedStopTimes;
            setTrip(trip, true);
            setUpdated(true);
        } else {
            // Use the onChange function to update the stop
            onChange(changes);
        }
    };

    const handleArrivalDateChange = (value) => {
        if (!stop || !value) return;

        const selectedDate = dayjs(value).startOf('day');
        const baseDate = trip.getArrivalTimeAsDayjs(stop).clone().startOf('day');
        let daysDiff = selectedDate.diff(baseDate, 'days');
        
        if (daysDiff !== 0) {
            const arrivalTimeDiff = daysDiff * 24 * 60 * 60;
            
            // If cumulative update is enabled, adjust departure time and cascade
            if (cumulativeUpdate) {
                const updatedStopTimes = [...stopTimes];
                
                // Update the current stop's arrival and departure times (maintain dwell time)
                updatedStopTimes[idx] = {
                    ...updatedStopTimes[idx],
                    arriveSecs: stop.arriveSecs + arrivalTimeDiff,
                    departSecs: stop.departSecs + arrivalTimeDiff
                };

                // Update subsequent stops by adding the departure time difference
                for (let i = idx + 1; i < updatedStopTimes.length; i++) {
                    updatedStopTimes[i] = {
                        ...updatedStopTimes[i],
                        arriveSecs: updatedStopTimes[i].arriveSecs + arrivalTimeDiff,
                        departSecs: updatedStopTimes[i].departSecs + arrivalTimeDiff
                    };
                }

                trip.stopTimes = updatedStopTimes;
                setTrip(trip, true);
                setUpdated(true);
            } else {
                const changes = {
                    arriveSecs: stop.arriveSecs + arrivalTimeDiff
                };
                onChange(changes);
            }
        }
    };

    const handleArrivalTimeChange = (e, time) => {
        if (!time) return;
        const value = dayjs(time, 'HH:mm');
        if (!value.isValid()) return;

        // Get the current stop's arrival time relative to the trip's date
        const currentArrivalTime = trip.getArrivalTimeAsDayjs(stop);
        const currentRelativeArrivalSecs = dayjsToSecsSinceMidnight(currentArrivalTime);
        
        // Convert the input time to seconds since midnight
        const newRelativeArrivalSecs = dayjsToSecsSinceMidnight(value);
        
        // Calculate the time difference in seconds
        const arrivalTimeDiff = newRelativeArrivalSecs - currentRelativeArrivalSecs;
        
        // If cumulative update is enabled, adjust departure time and cascade
        if (cumulativeUpdate) {
            const updatedStopTimes = [...stopTimes];

            // Update the current stop's arrival and departure times (maintain dwell time)
            updatedStopTimes[idx] = {
                ...updatedStopTimes[idx],
                arriveSecs: stop.arriveSecs + arrivalTimeDiff,
                departSecs: stop.departSecs + arrivalTimeDiff
            };

            // Update subsequent stops by adding the departure time difference
            for (let i = idx + 1; i < updatedStopTimes.length; i++) {
                updatedStopTimes[i] = {
                    ...updatedStopTimes[i],
                    arriveSecs: updatedStopTimes[i].arriveSecs + arrivalTimeDiff,
                    departSecs: updatedStopTimes[i].departSecs + arrivalTimeDiff
                };
            }

            trip.stopTimes = updatedStopTimes;
            setTrip(trip, true);
            setUpdated(true);
        } else {
            const changes = {
                arriveSecs: stop.arriveSecs + arrivalTimeDiff
            };
            onChange(changes);
        }
    };

    // Add function to handle location changes
    const handleLocationChange = useCallback((value, option) => {
        if (!value || !option) {
            console.warn('[SimpleStopTimes] Invalid location data received');
            return;
        }

        const processLocationChange = async () => {
            try {
                const location = createLocationObject(option, value);
                if(!location) return;

                const savedStopDetails = await ensureStopHasId(location);
                
                // Merge full stop details from the saved stop into location
                if (savedStopDetails) {
                    Object.assign(location, savedStopDetails);
                }

                if (!stop) {
                    return;
                }

                const isPlaceholder = stop.placeholder;
                const isChangingExistingStop = stop.stopId !== location.stopId;

                if (isPlaceholder) {
                    await replacePlaceholder(stop, location);
                } else if (isChangingExistingStop) {
                    await handleStopReplacement(location);
                } else {
                    // Even if stopId hasn't changed, merge full details from savedStopDetails into route stop
                    const currentStopInRoute = route.stops[idx];
                    if (currentStopInRoute && savedStopDetails) {
                        Object.assign(currentStopInRoute, savedStopDetails);
                        
                        // Also update corresponding stop in all services
                        route.services.forEach(service => {
                            if (service.stops?.[idx]) {
                                Object.assign(service.stops[idx], savedStopDetails);
                            }
                        });
                        if (route.stopTimes?.[idx]) {
                            route.stopTimes[idx] = { ...route.stopTimes[idx], stopId: savedStopDetails.stopId };
                        }
                    }
                }

                // RETURN TRIP SYNC: If first stop location changed, update last stop too
                if (route.returnEnabled && idx === 0 && stopTimes.length > 2) {
                    const lastStopIdx = stopTimes.length - 1;
                    const lastStop = route.stops[lastStopIdx];
                    
                    // Verify last stop is actually a return stop (same stopId as first)
                    if (lastStop && route.stops[0].stopId === lastStop.stopId) {
                        // Remove old last stop
                        await route.removeStopAtIndex(lastStopIdx);
                        
                        // Add updated last stop with same location as new first stop (including full details)
                        await route.addStopAtIndex({
                            ...location,  // Use new location with full details
                            stopTimeId: lastStop.stopTimeId,  // Keep unique ID
                            arriveSecs: lastStop.arriveSecs,  // Keep its times
                            departSecs: lastStop.departSecs
                        }, lastStopIdx);
                    }
                }

                // Finalize route changes
                route.calculateStartEnd({stops: route.stops, firstStop: route.stops[0], lastStop: route.stops[route.stops.length - 1]});
                setRouteNow(route.clone());
            } catch (error) {
                console.error('[SimpleStopTimes] Error handling location change:', error);
            }
        };

        const createLocationObject = (option, value) => {
            const {lon, lat, stopName, stopId, place_id} = option;
            if(!lat || !lon) return null  // if lat or lon is not present or 0, then don't create a location object
            return {
                location: value,
                center: [lon, lat],
                stopType: 'charter',
                charterStopType: stop?.charterStopType || 'pickup', // Preserve existing charterStopType
                notes: stop?.notes || '',
                value,
                lat,
                lon,
                stopName,
                stopId,
                place_id,
            };
        };

        const ensureStopHasId = async (location) => {
            if (!location.stopId) {
                const existingStop = find(values(allStops), {place_id: location.place_id});
                if (existingStop) {
                    Object.assign(location, existingStop);
                    // Ensure existing stop is in cache
                    if (setAllStops && !allStops?.[location.stopId]) {
                        setAllStops(prev => ({
                            ...prev,
                            [location.stopId]: location
                        }));
                    }
                    return location; // Return the stop with full details
                }
                location.duplicate = -1;
                location.verified = 1;
                // Save returns the stop object - use it directly
                const savedStop = await stopModelData.save(location);
                location.stopId = savedStop.stopId;
                
                // Update cache directly - following standard pattern from RouteMap, ItineraryView, etc.
                if (setAllStops) {
                    setAllStops(prev => ({
                        ...prev,
                        [savedStop.stopId]: savedStop
                    }));
                }
                // Return the saved stop with full details
                return savedStop;
            } else {
                // If it already has stopId, just ensure it's in cache
                if (setAllStops && !allStops?.[location.stopId]) {
                    setAllStops(prev => ({
                        ...prev,
                        [location.stopId]: location
                    }));
                }
                // Return the stop from cache if available, otherwise return location
                return allStops?.[location.stopId] || location;
            }
        };


        const replacePlaceholder = async (placeholder, newLocation) => {
            try {
                // Critical: preserve stopTimeId for React key stability
                newLocation.stopTimeId = placeholder.stopTimeId;
                
                if (!newLocation.lat || !newLocation.lon) {
                    console.error('[SimpleStopTimes] Cannot replace placeholder: missing coordinates', newLocation);
                    return;
                }
                
                delete newLocation.placeholder;
                
                // Ensure stop is in cache (already handled by ensureStopHasId, but double-check)
                // Also merge full details from allStops if available
                const fullStopFromCache = allStops?.[newLocation.stopId];
                if (fullStopFromCache) {
                    Object.assign(newLocation, fullStopFromCache);
                }
                if (setAllStops && newLocation.stopId && !allStops?.[newLocation.stopId]) {
                    setAllStops(prev => ({
                        ...prev,
                        [newLocation.stopId]: newLocation
                    }));
                }
                
                const placeholderIdx = route.stops.findIndex(s => s.stopTimeId === placeholder.stopTimeId);
                if (placeholderIdx === -1) {
                    console.error('[SimpleStopTimes] Placeholder not found in route.stops');
                    return;
                }
                
                const realStopCount = getRealStops(route.stops).length;
                const isFirstRealStop = realStopCount === 0;
                const isFirstPosition = placeholderIdx === 0;
                const prevReal = findPreviousRealStop(route.stops, placeholderIdx);
                const nextReal = findNextRealStop(route.stops, placeholderIdx);
                const prevStopInRoute = prevReal ? route.stops.find(s => s.stopTimeId === prevReal.stopTimeId) : null;
                const nextStopInRoute = nextReal ? route.stops.find(s => s.stopTimeId === nextReal.stopTimeId) : null;

                // CRITICAL: In return mode, ensure we never insert after the return stop
                let insertBeforeReturnStop = false;
                let returnStopIdx = -1;
                if (route.returnEnabled) {
                    const mirrorStop = getMirrorStop(route.stops);
                    if (mirrorStop) {
                        returnStopIdx = route.stops.findIndex(s => s.stopTimeId === mirrorStop.stopTimeId);
                        // If placeholder is before the return stop and we're appending, insert before return stop instead
                        if (returnStopIdx !== -1 && placeholderIdx < returnStopIdx && !nextStopInRoute) {
                            insertBeforeReturnStop = true;
                        }
                    }
                }

                const originalStopTimeId = placeholder.stopTimeId;
                const tempStopTimeId = ulid();
                const insertionPayload = {
                    ...newLocation,
                    stopTimeId: tempStopTimeId,
                    placeholder: false
                };
                
                // 1. Insert the new stop (with a temporary stopTimeId) while the placeholder still exists
                if (isFirstRealStop) {
                    await route.addFirstStop(insertionPayload);
                } else if (isFirstPosition) {
                    await route.addStopAtIndex(insertionPayload, 0);
                } else if (prevStopInRoute && nextStopInRoute) {
                    await route.insertStopBetweenStops(insertionPayload, prevStopInRoute, nextStopInRoute);
                } else if (insertBeforeReturnStop && returnStopIdx !== -1) {
                    await route.addStopAtIndex(insertionPayload, returnStopIdx);
                } else if (prevStopInRoute) {
                    await route.appendStop(insertionPayload, null, true);
                } else if (nextStopInRoute) {
                    await route.addStopAtIndex(insertionPayload, Math.max(0, placeholderIdx));
                } else {
                    await route.addStopAtIndex(insertionPayload, Math.max(0, Math.min(placeholderIdx, route.stops.length)));
                }
                
                // 2. Remove the old placeholder (still carrying the original stopTimeId)
                await route.removeStop(placeholder);
                
                // 3. Swap the temporary stopTimeId back to the original for consistency/react keys
                const finalIdx = route.stops.findIndex(s => s.stopTimeId === tempStopTimeId);
                
                if (finalIdx !== -1) {
                    newLocation.stopTimeId = originalStopTimeId;
                    const routeStop = route.stops[finalIdx];
                    const routeStopTime = route.stopTimes[finalIdx];
                    Object.assign(routeStop, newLocation, { placeholder: false, stopTimeId: originalStopTimeId });
                    Object.assign(routeStopTime, { stopId: newLocation.stopId, stopTimeId: originalStopTimeId });
                    
                    route.services.forEach(service => {
                        if (service.stops?.[finalIdx]) {
                            Object.assign(service.stops[finalIdx], newLocation, { placeholder: false, stopTimeId: originalStopTimeId });
                        }
                        if (service.stopTimes?.[finalIdx]) {
                            Object.assign(service.stopTimes[finalIdx], { stopId: newLocation.stopId, stopTimeId: originalStopTimeId });
                        }
                    });
                } else {
                    console.warn('[SimpleStopTimes] Inserted stop not found after replacing placeholder', { tempStopTimeId });
                }
                
                
                // Update mirror stop if in return mode (only relevant for first position)
                if (route.returnEnabled && isFirstPosition) {
                    const mirror = getMirrorStop(route.stops);
                    if (mirror) {
                        mirror.lat = newLocation.lat;
                        mirror.lon = newLocation.lon;
                        mirror.stopId = newLocation.stopId;
                        mirror.stopName = newLocation.stopName;
                        mirror.address = newLocation.address;
                        delete mirror.placeholder;
                    }
                }
                
                // CRITICAL FIX: Rebuild waypoints to calculate route path
                // await route.rebuildWaypoints();
                
                route.calculateStartEnd({
                    stops: route.stops,
                    firstStop: route.stops[0],
                    lastStop: route.stops[route.stops.length - 1],
                    trim: true
                });
                
                // Update map viewport to fit new stop
                if (fitMapToRoute) {
                    fitMapToRoute(route.stops, { force: true });
                }
            } catch (error) {
                console.error('[SimpleStopTimes] Error replacing placeholder:', error);
                throw error;
            }
        };

        const handleStopReplacement = async (location) => {
            // Merge full details from allStops if available
            const fullStopFromCache = allStops?.[location.stopId];
            if (fullStopFromCache) {
                Object.assign(location, fullStopFromCache);
            }
            // Ensure stop is in cache (already handled by ensureStopHasId, but double-check)
            if (setAllStops && location.stopId && !allStops?.[location.stopId]) {
                setAllStops(prev => ({
                    ...prev,
                    [location.stopId]: location
                }));
            }
            // Remove the existing stop and add the new one in the same position
            await route.removeStopAtIndex(idx);
            await route.addStopAtIndex(location, idx);
            
            // Rebuild waypoints and recalculate route
            await route.rebuildWaypoints();
            route.calculateStartEnd({stops: route.stops, firstStop: route.stops[0], lastStop: route.stops[route.stops.length - 1], trim: true});

                // If this is a return trip and first stop was changed, update the last stop too
            if (route.returnEnabled && idx === 0 && trip.stopTimes.filter(s => s.stopId).length > 1) {
                const lastStopIdx = stopTimes.length - 1;
                if (lastStopIdx > 0) {
                    await route.removeStopAtIndex(lastStopIdx);
                    await route.addStopAtIndex({...location, stopTimeId: ulid()}, lastStopIdx);
                    await route.rebuildWaypoints();
                    route.calculateStartEnd({stops: route.stops, firstStop: route.stops[0], lastStop: route.stops[route.stops.length - 1], trim: true});
                }
            }
        };

        processLocationChange();
    }, [route, stopTimes, stop, idx, setRouteNow, allStops, returnEnabled, setAllStops, fitMapToRoute]);

    // Add function to handle adding a new stop
    const handleAddStop = async () => {
        try {
            // Create a new stop object with default values
            handleAddStopTime(idx + 1);
        } catch (error) {
            console.error('[SimpleStopTimes] Error adding new stop:', error);
        }
    };


    const arrivalTime = useMemo(() => {
        if (trip && stop) {
            if(idx > 0) {
                const arrivalDate = trip.getArrivalTimeAsDayjs(stop);
                const previousDepartDate = trip.getDepartureTimeAsDayjs(trip.stopTimes[idx - 1]);
                const diffDays = arrivalDate.clone().startOf('day').diff(previousDepartDate.clone().startOf('day'), 'days');
                const result = `${diffDays !== 0 ? `(${diffDays > 0 ? '+' : ''}${diffDays}) ` : ''}${arrivalDate.format('HH:mm')}`;
                return result;
            }
        }
    }, [trip, stop, idx]);

    const departTime = useMemo(() => {
        if (trip && stop) {
            return trip.getDepartureTimeAsDayjs(stop)?.format('HH:mm');
        }
    }, [trip, stop]);

    const showAddStop = useMemo(() => {
        // return stopTimes.filter(s => !s.stopId).length === 0 && isLast;
        return isLast;
    }, [isLast]);

    const isArrivalDateTimeEarlier = useMemo(() => {
        if (!trip || !stop || idx <= 0) return false;
        const currentArrival = trip.getArrivalTimeAsDayjs(stop);
        const prevDepart = trip.getDepartureTimeAsDayjs(trip.stopTimes[idx - 1]);
        if (!currentArrival || !prevDepart || !currentArrival.isValid?.() || !prevDepart.isValid?.()) return false;
        return currentArrival.isBefore(prevDepart);
    }, [trip, stop, idx]);

    const arrDepConflict = useMemo(() => {
        if (!trip || !stop || isFirst || isLast) return false;
        const arr = trip.getArrivalTimeAsDayjs(stop);
        const dep = trip.getDepartureTimeAsDayjs(stop);
        if (!arr || !dep || !arr.isValid?.() || !dep.isValid?.()) return false;
        return dep.isBefore(arr);
    }, [trip, stop, isFirst, isLast]);

    const { showArrivalError, showDepartError, arrivalErrorTitle } = useMemo(() => {
        const type = getStopType();
        const arrivalRelevant = type !== 'pickup';
        const departRelevant = type !== 'dropoff';
        const hasArr = stop?.arriveSecs !== undefined && stop?.arriveSecs !== null;
        const hasDep = stop?.departSecs !== undefined && stop?.departSecs !== null;

        const arr = trip?.getArrivalTimeAsDayjs?.(stop);
        const dep = trip?.getDepartureTimeAsDayjs?.(stop);
        const prevDep = (idx > 0) ? trip?.getDepartureTimeAsDayjs?.(trip.stopTimes[idx - 1]) : null;

        const sameStopInvalid = !!(arr && dep && arr.isValid?.() && dep.isValid?.() && dep.isBefore(arr));
        const prevInvalid = !!(arr && prevDep && arr.isValid?.() && prevDep.isValid?.() && arr.isBefore(prevDep));

        const showArr = arrivalRelevant && hasArr && (prevInvalid || (hasDep && sameStopInvalid));
        const showDep = departRelevant && hasArr && hasDep && sameStopInvalid;

        let title = undefined;
        if (showArr) {
            title = prevInvalid ? 'Drop-off must be on or after previous Pick-up time' : 'Drop-off must be on or before Pick-up time';
        }

        return { showArrivalError: showArr, showDepartError: showDep, arrivalErrorTitle: title };
    }, [trip, stop, idx, arrDepConflict]);

    return (
        <>
            <div
                ref={setNodeRef}
                style={style}
                className={'stop-item'}
                onMouseOver={() => {
                    if (!editing) {
                        setFocusStop({ ...stop, ...allStops[stop?.stopId] });
                    }
                }}
            >
                <Card
                    size="small"
                    className="stop-card"
                    key={`StopTime-Row-${stop?.stopTimeId}`}
                    title={
                        <div className="stop-header" onClick={handleClick}>
                            <div className='d-flex align-items-center'>
                                {/* DRAG HANDLE: Only show if stop can be dragged
                                    Hide for: immutable routes, viewOnly mode, 
                                    and the last stop in Return Trip mode */}
                                {!immutable && !viewOnly && !(returnEnabled && isLast) && (
                                    <div
                                        className="drag-handle mr-2"
                                        {...attributes}
                                        {...listeners}
                                    >
                                        <DragOutlined />
                                    </div>
                                )}
                                <div className="d-flex align-items-center">
                                    {isFirst ? (
                                        <div className="mr-2">
                                            <Pin size={24} type="bus" sequence="1" />
                                        </div>
                                    ) : isLast && !returnEnabled ? (
                                        <div className="mr-2">
                                            <Pin size={24} type="bus" sequence={index} />
                                        </div>
                                    ) : (
                                        <div className="mr-2">
                                            <Pin size={24} type="bus" sequence={index} />
                                        </div>
                                    )}
                                    <span>
                                        Stop {index} - {getStopTypeLabel()}
                                    </span>
                                </div>
                                {stopValidationMessages?.length > 0 && !stop.placeholder && (
                                    <Tooltip
                                        title={
                                            <div>
                                                {stopValidationMessages.map((m, i) => (
                                                    <p key={`vl_${i}`}>{m.msg}</p>
                                                ))}
                                            </div>
                                        }
                                    >
                                        <span className="validation-icon ml-2"><Warning style={{ height: 14 }} /></span>
                                    </Tooltip>
                                )}
                            </div>
                        </div>
                    }
                    {...viewOnly ? {
                        extra: <></>
                    } : {
                        extra:
                            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                {/* Notes toggle button */}
                                <Button 
                                    className={`py-0 ${showNotes ? 'showNotes' : ''}`}
                                    type="text"
                                    icon={<Notes2 style={{ height: 14 }} />}
                                    onClick={() => setShowNotes(!showNotes)}
                                    style={{ 
                                        height: 24, 
                                        width: 24,
                                        opacity: showNotes ? 1 : 0.6,
                                        outline: 'none'
                                    }}
                                />
                                {/* Show delete button for all stops except when there's only one stop */}
                                {trip.stops.length >= 2 && (
                                    <Button className='py-0'
                                        type="text"
                                        icon={<Trash style={{ height: 14 }} />}
                                        onClick={() => onDelete(idx)}
                                        style={{ height: 24, width: 24, outline: 'none' }}
                                    />
                                )}
                            </div>
                    }}
                >
                    <Space direction="vertical" style={{ width: '100%', gap: 4 }}>
                        {isRecalculating && (
                            <div style={{ fontSize: '12px', color: '#999', padding: '4px 0' }}>
                                <SyncOutlined spin /> Recalculating route...
                            </div>
                        )}
                        {viewOnly ?
                            <div className='text-muted font-weight-normal'>
                                {allStops[stop?.stopId]?.stopName || stop?.stopName || stop?.location}
                            </div>
                            :
                            // <>
                            <Row gutter={8}>
                                <Col span={24}>
                                    <Form.Item label={`${getStopTypeLabel()} Location`}>
                                        <LocationLookup
                                            onClick={() => {
                                            }}
                                            location={{ value: stop?.location || stop?.stopName || stop?.stopId, label: stop?.stopId ? <DisplayStop stop={stop} noWarning={true} noCode={true} noRoutes={true} noInterchange={true} showOpenInNewTab={true} /> : stop?.location }}
                                            setLocation={handleLocationChange}
                                            placeholder="Enter location"
                                            style={{ width: '100%' }}
                                            defaultValue={stop?.location || allStops[stop?.stopId]?.stopName || stop?.stopName}
                                            allStops={allStops}
                                            type="google"
                                            route={route}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        }        
                        {(isFirst || isLast) && (
                            <Row gutter={8}>
                                <Col span={12} className="time-field-col">
                                    <Form.Item label={isFirst ? "Pick-up Time" : "Drop-off Time"}>
                                        <div className="time-field-container" style={{ position: 'relative' }}>
                                            <TimeField
                                                className={`ant-input ant-input-sm inputtext-small text-center`}
                                                style={{ 
                                                    width: '100%', 
                                                    paddingRight: (isFirst && isEditingDeparture) || (isLast && isEditingArrival) ? '50px' : '30px',
                                                    opacity: (isFirst && isEditingDeparture) || (isLast && isEditingArrival) ? 1 : 0.5,
                                                    backgroundColor: (isFirst && isEditingDeparture) || (isLast && isEditingArrival) ? '#fff' : '#f5f5f5',
                                                    cursor: (isFirst && isEditingDeparture) || (isLast && isEditingArrival) ? 'text' : 'pointer'
                                                }}
                                                value={(isFirst && isEditingDeparture) ? (trip.getDepartureTimeAsDayjs(stop)?.format?.('HH:mm') || '') : 
                                                        (isLast && isEditingArrival) ? (trip.getArrivalTimeAsDayjs(stop)?.format?.('HH:mm') || '') : ''}
                                                format="HH:mm"
                                                placeholder="Click to set time"
                                                onChange={(isFirst && isEditingDeparture) ? (e, time) => handleDepartTimeChange(e, time) : 
                                                        (isLast && isEditingArrival) ? (e, time) => handleArrivalTimeChange(e, time) : undefined}
                                                onFocus={() => {
                                                    if (isFirst) {
                                                        // First stop should always be pickup only
                                                        if (getStopType() !== 'pickup') {
                                                            handleStopTypeChange('pickup');
                                                        }
                                                    } else if (isLast && getStopType() !== 'dropoff') {
                                                        handleStopTypeChange('dropoff');
                                                    }
                                                }}
                                                onClick={() => {
                                                    if (isFirst) {
                                                        // First stop should always be pickup only
                                                        if (getStopType() !== 'pickup') {
                                                            handleStopTypeChange('pickup');
                                                        }
                                                    } else if (isLast && getStopType() !== 'dropoff') {
                                                        handleStopTypeChange('dropoff');
                                                    }
                                                }}
                                            />
                                            <Time className="time-field-icon" />
                                        </div>
                                    </Form.Item>
                                </Col>
                            </Row>
                        )}
                            {/* </> */}
                        {/* } */}

                        {/* Show estimated arrival time right after stop location for middle and last stops */}
                        {(() => {
                            // Only show ETA when there's a day difference (contains + or -)
                            const hasDayDifference = arrivalTime && (arrivalTime.includes('+') || arrivalTime.includes('-'));
                            const shouldShow = !viewOnly && !isFirst && hasDayDifference && !editingETA;
                            return shouldShow;
                        })() && (
                            <div style={{ 
                                fontSize: '14px', 
                                color: '#687179',
                                padding: '4px 8px',
                                backgroundColor: '#F4F7FB',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}>
                                <span>ETA: {arrivalTime}</span>
                                <div className="d-flex align-items-center" style={{ gap: '6px' }}>
                                    {/* Only show the popconfirm if the arrival time is earlier than the last stop */}
                                    {isArrivalDateTimeEarlier && (
                                        <Popconfirm
                                            title="Earlier Time Detected"
                                            description={
                                                <div style={{maxWidth: '220px'}}>
                                                    <p style={{marginBottom: '8px', lineHeight: '1.3'}}>Entered time is earlier than the last stop.</p>
                                                    <p style={{marginBottom: '4px', lineHeight: '1.2'}}>Shift to next day?</p>
                                                </div>
                                            }
                                            onConfirm={() => setEditingETA(true)}
                                            okText="Confirm"
                                            cancelText="Cancel"
                                            okType="primary"
                                            placement="topLeft"
                                            trigger="hover"
                                        >
                                            <span className="validation-icon" style={{ cursor: 'pointer' }}>
                                                <Warning style={{ height: 14 }} />
                                            </span>
                                        </Popconfirm>
                                    )}
                                    <Button 
                                        onClick={() => {
                                            setEditingETA(true);
                                            if (getStopType() === 'pickup') {
                                                handleStopTypeChange('both');
                                            }
                                        }} 
                                        type="primary" 
                                        className="icon-button btn-sm print" 
                                        icon={<Edit />} 
                                        size="small"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Editing section for arrival time - appears right after ETA */}
                        {!viewOnly && !isFirst && editingETA && (
                            <div style={{
                                padding: '4px 8px',
                                backgroundColor: '#F4F7FB',
                                borderRadius: '6px'
                            }}>
                                <Row gutter={8}>
                                    <Col span={12}>
                                        <Form.Item label="Arrival Date" size="small">
                                            <DatePicker
                                                style={{ width: '100%' }}
                                                value={trip.getArrivalTimeAsDayjs(stop)}
                                                format="DD/MM/YYYY"
                                                onChange={(value) => {
                                                    handleArrivalDateChange(value);
                                                }}
                                                autoFocus
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item label="Arrival Time" size="small">
                                            <div className="time-field-container">
                                                <TimeField
                                                    className={`ant-input ant-input-sm inputtext-small text-center`}
                                                    style={{ width: '100%', paddingRight: '30px' }}
                                                    value={trip.getArrivalTimeAsDayjs(stop)?.format?.('HH:mm')}
                                                    format="HH:mm"
                                                    onChange={(e, time) => {
                                                        handleArrivalTimeChange(e, time);
                                                    }}
                                                />
                                                <Time className="time-field-icon" />
                                            </div>
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <div style={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center',
                                    marginTop: '8px',
                                    paddingTop: '8px',
                                    borderTop: '1px solid #e8e8e8'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <div style={{ fontSize: '13px', color: '#666', lineHeight: '1.2' }}>
                                            Changes will {cumulativeUpdate && !isLast ? 'cascade to subsequent stops' : 'only affect this stop'}
                                        </div>
                                    </div>
                                    <Button 
                                        type="primary"
                                        size="small" 
                                        onClick={() => setEditingETA(false)}
                                        className="icon-button btn-filled btn-success btn-sm"
                                        icon={<Check />}
                                    >Done
                                    </Button>
                                </div>
                            </div>
                        )}

                        {!viewOnly && !isFirst && !isLast &&
                            <Row gutter={8}>
                                {/* Time fields - side by side for middle stops only */}
                                <Col span={12} className="time-field-col">
                                    <Form.Item label="Drop-off Time">
                                        <Tooltip title={showArrivalError ? arrivalErrorTitle : undefined} placement="topRight">
                                            <div className="time-field-container" style={{ position: 'relative', border: showArrivalError ? '1px solid #ff4d4f' : undefined, borderRadius: showArrivalError ? 7 : undefined }}>
                                            <TimeField
                                                className={`ant-input ant-input-sm inputtext-small text-center`}
                                                style={{ 
                                                    width: '100%', 
                                                    paddingRight: isEditingArrival ? '50px' : '30px',
                                                    opacity: isEditingArrival ? 1 : 0.5,
                                                    backgroundColor: isEditingArrival ? '#fff' : '#f5f5f5',
                                                    cursor: isEditingArrival ? 'text' : 'pointer'
                                                }}
                                                value={isEditingArrival ? (trip.getArrivalTimeAsDayjs(stop)?.format?.('HH:mm') || '') : ''}
                                                format="HH:mm"
                                                placeholder="Click to set time"
                                                onChange={isEditingArrival ? (e, time) => handleArrivalTimeChange(e, time) : undefined}
                                                onFocus={() => {
                                                    const currentType = getStopType();
                                                    if (currentType === 'pickup') {
                                                        handleStopTypeChange('both');
                                                    } else if (currentType === 'dropoff') {
                                                        // Already editing arrival, no change needed
                                                    }
                                                }}
                                                onClick={() => {
                                                    const currentType = getStopType();
                                                    if (currentType === 'pickup') {
                                                        handleStopTypeChange('both');
                                                    } else if (currentType === 'dropoff') {
                                                        // Already editing arrival, no change needed
                                                    }
                                                }}
                                            />
                                            <Time className="time-field-icon" />
                                            {isEditingArrival && (
                                                <Button 
                                                    type="text"
                                                    size="small"
                                                    onClick={() => {
                                                        const currentType = getStopType();
                                                        if (currentType === 'both') {
                                                            handleStopTypeChange('pickup');
                                                        } else if (currentType === 'dropoff') {
                                                            handleStopTypeChange('pickup');
                                                        }
                                                    }}
                                                    className="time-field-close-btn"
                                                    style={{ 
                                                        position: 'absolute', 
                                                        right: '6px', 
                                                        top: '6px',
                                                        padding: '0',
                                                        width: '17px',
                                                        height: '17px',
                                                        minWidth: '17px',
                                                        fontSize: '14px',
                                                        lineHeight: '1',
                                                        borderRadius: '50%',
                                                        backgroundColor: '#BFBFBF',
                                                        color: '#fff',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        zIndex: 1
                                                    }}
                                                >
                                                    ×
                                                </Button>
                                            )}
                                            </div>
                                        </Tooltip>
                                    </Form.Item>
                                </Col>
                                <Col span={12} className="time-field-col">
                                    <Form.Item label="Pick-up Time">
                                        <Tooltip title={showDepartError ? 'Pick-up must be on or after ETA' : undefined} placement="topRight">
                                            <div className="time-field-container" style={{ position: 'relative', border: showDepartError ? '1px solid #ff4d4f' : undefined, borderRadius: showDepartError ? 7 : undefined }}>
                                            <TimeField
                                                className={`ant-input ant-input-sm inputtext-small text-center`}
                                                style={{ 
                                                    width: '100%', 
                                                    paddingRight: isEditingDeparture ? '50px' : '30px',
                                                    opacity: isEditingDeparture ? 1 : 0.5,
                                                    backgroundColor: isEditingDeparture ? '#fff' : '#f5f5f5',
                                                    cursor: isEditingDeparture ? 'text' : 'pointer'
                                                }}
                                                value={isEditingDeparture ? (trip.getDepartureTimeAsDayjs(stop)?.format?.('HH:mm') || '') : ''}
                                                format="HH:mm"
                                                placeholder="Click to set time"
                                                onChange={isEditingDeparture ? (e, time) => handleDepartTimeChange(e, time) : undefined}
                                                onFocus={() => {
                                                    const currentType = getStopType();
                                                    if (currentType === 'dropoff') {
                                                        handleStopTypeChange('both');
                                                    } else if (currentType === 'pickup') {
                                                        // Already editing departure, no change needed
                                                    }
                                                }}
                                                onClick={() => {
                                                    const currentType = getStopType();
                                                    if (currentType === 'dropoff') {
                                                        handleStopTypeChange('both');
                                                    } else if (currentType === 'pickup') {
                                                        // Already editing departure, no change needed
                                                    }
                                                }}
                                            />
                                            <Time className="time-field-icon" />
                                            {isEditingDeparture && (
                                                <Button 
                                                    type="text"
                                                    size="small"
                                                    onClick={() => {
                                                        const currentType = getStopType();
                                                        if (currentType === 'both') {
                                                            handleStopTypeChange('dropoff');
                                                        } else if (currentType === 'pickup') {
                                                            handleStopTypeChange('dropoff');
                                                        }
                                                    }}
                                                    className="time-field-close-btn"
                                                    style={{ 
                                                        position: 'absolute', 
                                                        right: '6px', 
                                                        top: '6px',
                                                        padding: '0',
                                                        width: '17px',
                                                        height: '17px',
                                                        minWidth: '17px',
                                                        fontSize: '14px',
                                                        lineHeight: '1',
                                                        borderRadius: '50%',
                                                        backgroundColor: '#BFBFBF',
                                                        color: '#fff',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        zIndex: 1
                                                    }}
                                                >
                                                    ×
                                                </Button>
                                            )}
                                            </div>
                                        </Tooltip>
                                    </Form.Item>
                                </Col>
                            </Row>}

                        {/* Notes field - shown when toggled or when there's existing text */}
                        {!viewOnly && showNotes && (
                            <div className="time-field-col">
                                <Form.Item label={`${getStopTypeLabel()} Notes`}>
                                    <Input
                                        value={localNotes}
                                        onChange={(e) => handleNotesChange(e.target.value)}
                                        onBlur={persistNotes}
                                        onPressEnter={persistNotes}
                                        placeholder={`Enter ${getStopTypeLabel().toLowerCase()} notes...`}
                                    />
                                </Form.Item>
                            </div>
                        )}

                    </Space>
                </Card>
            </div>
            {showAddStop && <div className='button-separator'>
                <Button type="primary" className="icon-button" icon={<PlusOutlined />} onClick={handleAddStop}>Add Stop</Button>
            </div>}
            {stop?.transfersTo?.length > 0 && (
                <Card size="small" className="transfer-card mt-2">
                    <div className="transfer-header">
                        <EyeOutlined className="mr-2" />
                        <span>Transfers</span>
                    </div>
                    <div className="transfer-routes">
                        {stop?.transfersTo?.map((tx, idx) => (
                            <div
                                key={'tt_' + idx}
                                className={`transfer-route ${tx.invalid ? "transfer-warning" : ""} ${focusTransfers.to.filter(t => t.routeId === tx.routeId).length ? 'Selected' : ''}`}
                                onClick={e => {
                                    if (e.metaKey) return;
                                    e.stopPropagation();
                                    setFocusTransfers(focusTransfers => {
                                        if (find(focusTransfers.to, ['routeId', tx.routeId])) {
                                            focusTransfers.to = focusTransfers.to.filter(t => t.routeId !== tx.routeId);
                                        } else {
                                            focusTransfers.to = focusTransfers.to.concat(tx);
                                        }
                                        return {
                                            ...focusTransfers,
                                            stop: focusTransfers.from?.length || focusTransfers.to?.length ? { ...stop, ...allStops[stop.stopId] } : null
                                        };
                                    });
                                }}
                            >
                                <RouteNumber
                                    key={'TxRidTo' + tx.routeId}
                                    route={tx}
                                    link={true}
                                    meta={true}
                                    tooltip={tx.invalid && tx.warnings?.length ? uniq(tx.warnings).map(w => <p>{w}</p>) : null}
                                />
                            </div>
                        ))}
                    </div>
                </Card>
            )}
            {stop?.transfersFrom?.length > 0 && (
                <Card size="small" className="transfer-card mt-2">
                    <div className="transfer-header">
                        <EyeOutlined className="mr-2" />
                        <span>Pickups</span>
                    </div>
                    <div className="transfer-routes">
                        {stop?.transfersFrom?.map((tx, idx) => (
                            <div
                                key={'tf_' + idx}
                                className={`transfer-route ${tx.invalid ? "transfer-warning" : ""} ${focusTransfers.from.filter(t => t.routeId === tx.routeId).length ? 'Selected' : ''}`}
                                onClick={e => {
                                    if (e.metaKey) return;
                                    e.stopPropagation();
                                    setFocusTransfers(focusTransfers => {
                                        if (find(focusTransfers.from, ['routeId', tx.routeId])) {
                                            focusTransfers.from = focusTransfers.from.filter(t => t.routeId !== tx.routeId);
                                        } else {
                                            focusTransfers.from = focusTransfers.from.concat(tx);
                                        }
                                        return {
                                            ...focusTransfers,
                                            stop: focusTransfers.from?.length || focusTransfers.to?.length ? { ...stop, ...allStops[stop?.stopId] } : null
                                        };
                                    });
                                }}
                            >
                                <RouteNumber
                                    key={'TxRidFrom' + tx.routeId}
                                    route={tx}
                                    link={true}
                                    meta={true}
                                    tooltip={tx.invalid && tx.warnings?.length ? uniq(tx.warnings).map(w => <p>{w}</p>) : null}
                                />
                            </div>
                        ))}
                    </div>
                </Card>
            )}
        </>
    );
}

export default function SimpleStopTimes({
    route, charter,
    allStops,
    showSeconds,
    cumulativeUpdate,
    trip,
    setTrip,
    setRoute,
    setRouteNow,
    showAllStops,
    editingRoute,
    setFocusStop,
    setSelectedStop,
    selectedStop,
    setUpdated = noop,
    immutable = false,
    setFocusTransfers,
    focusTransfers,
    operator, validationHelper = { isValid: true },
    buildToStop,
    showNewStop, setShowNewStop,
    setAllStops,
}) {
    const { isAdmin, messageApi } = useAppContext();
    const [editing, setEditing] = useState(true);
    const [executeScroll, scrollRef] = useScroll();
    const [focus, setFocus] = useState(null);
    // Track when route recalculation is happening in background
    const [isRecalculating, setIsRecalculating] = useState(false);
    // Local state for stops array - updated synchronously for instant drag feedback
    const [activeStops, setActiveStops] = useState(trip.stops);
    const { allDepots } = useAllStops();
    const { allVehicleTypes, allVehicleTypesArray } = useAllVehicleTypes();

    // Sync local stops state with prop changes (for operations like delete, add, etc.)
    useEffect(() => {
        setActiveStops(trip.stops);
    }, [trip.stops]);

    useEffect(() => {
        executeScroll();
    }, [selectedStop, executeScroll]);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 10, // Only activate after dragging 10px
                delay: 250, // Only activate after 250ms
            }
        })
    );

    // Initialize map viewport helper with setFocusStop bound
    const fitMapToRoute = useMemo(() => createFitMapToRoute(setFocusStop), [setFocusStop]);

    const handleAddStopTime = useCallback((idx = trip.stops.length-2, placeholder = true) => {
        // Enforce placeholder limit
        if (placeholder) {
            const currentPlaceholders = getPlaceholders(route.stops);
            if (currentPlaceholders.length >= MAX_PLACEHOLDERS) {
                messageApi.warning(`Maximum ${MAX_PLACEHOLDERS} empty stops allowed`);
                return;
            }
        }
        
        // Adjust index for return mode (insert before mirror stop)
        let _idx = idx;
        if (route.returnEnabled) {
            const mirrorStop = getMirrorStop(route.stops);
            if (mirrorStop) {
                const mirrorIdx = route.stops.findIndex(s => s.stopTimeId === mirrorStop.stopTimeId);
                if (idx >= mirrorIdx) {
                    _idx = mirrorIdx; // Insert before mirror
                }
            }
        }
        
        // Determine the appropriate stop type based on position
        let stopType = 'dropoff'; // Default for middle stops
        if (_idx === 0) {
            stopType = 'pickup'; // First stop should be pickup
        } else if (_idx === trip.stops.length) {
            stopType = 'dropoff'; // Last stop should be dropoff
        }
        
        const newStopTime = {
            stopTimeId: ulid(),
            arriveSecs: isFinite(_idx) ? trip.stopTimes[_idx - 1]?.arriveSecs || 0 : last(trip.stopTimes)?.arriveSecs || 0,
            departSecs: isFinite(_idx) ? trip.stopTimes[_idx - 1]?.departSecs || 0 : last(trip.stopTimes)?.departSecs || 0,
            sequence: isFinite(_idx) ? _idx : 1,
            timingPoint: true,
            placeholder,
            charterStopType: stopType,
            notes: ''
        };
        
        route.addStopAtIndex(newStopTime, _idx);

        setRouteNow(route.clone());
        setUpdated(true);
    }, [route, messageApi]);

    useEffect(function ensure1PlaceholderStop() {
        if (!trip) return;

        // If no stops at all, add both placeholder stops at once
        if (!trip.stops?.length) {
            const stop1 = {
                stopTimeId: ulid(),
                arriveSecs: 9 * 3600,
                departSecs: 9 * 3600,
                timingPoint: true,
                placeholder: true,
                charterStopType: 'pickup',
                notes: ''
            };

            trip.date = charter.dates?.[0];
            route.addStopAtIndex(stop1, 0);
            setRouteNow(route.clone());
            setUpdated(true);
        }
    }, [trip, setRouteNow, setUpdated]);

    const calculateArrivalTime = async (fromStop, toStop) => {
        if(!fromStop?.lat || !fromStop?.lon || !toStop?.lat || !toStop?.lon) {
            return dayjs();
        }
        const {duration} = await getCachedPathDebounced({profile: 'bus', startCoord: fromStop, endCoord: toStop});
        const fromTime = getDepartureTimeAsDayjs(trip, fromStop);
        if (!fromTime) {
            console.warn('[SimpleStopTimes] Missing departure time in calculateArrivalTime', { fromStop });
            return dayjs().add(30, 'minute'); // Fallback to current time + 30 minutes
        }
        return fromTime.add(duration, 'seconds');
    };

    const handleDragEnd = useCallback((event) => {
        const { active, over } = event;
        
        // VALIDATION: Check if we have valid drag source and destination
        if (!active?.id || !over?.id || active.id === over.id) {
            return; // Either invalid or no actual movement happened
        }

        // FIND INDICES: Locate which stops were involved in the drag
        // Read from LOCAL STATE for instant feedback
        const oldIndex = activeStops.findIndex((stop) => getStableStopId(stop) === active.id);
        const newIndex = activeStops.findIndex((stop) => getStableStopId(stop) === over.id);
        
        // SAFETY CHECK: Make sure we found both stops
        if (oldIndex === -1 || newIndex === -1) {
            console.error('[SimpleStopTimes] Drag and drop error: Could not find stop indices in activeStops');
            return;
        }
        
        const draggedStop = activeStops[oldIndex];
        
        // MIRROR STOP RESTRICTION: Cannot drag mirror stops in return mode
        if (draggedStop.isReturnStop) {
            messageApi.warning('Cannot reorder return stop.');
            return;
        }
        
        // RETURN TRIP RESTRICTION: Cannot drag anything to last position (reserved for mirror)
        if (route.returnEnabled) {
            const lastIndex = activeStops.length - 1;
            
            // Block if trying to move something to last position
            if (newIndex === lastIndex) {
                return; // Silently prevent the move
            }
        }

        // STEP 1: IMMEDIATE UI UPDATE (Priority 1: Make it work + Priority 2: Performance)
        // Use arrayMove to reorder - this is fast and happens instantly
        // Create new array from LOCAL STATE
        const reorderedStops = arrayMove(activeStops, oldIndex, newIndex);
        
        // UPDATE STOP TYPES based on new positions after reordering
        // First stop must always be pickup, last stop must always be dropoff
        // In Return Trip mode with more than 2 stops, the last stop is a return stop (mirrors first)
        reorderedStops.forEach((stop, idx) => {
            const isFirst = idx === 0;
            // Last stop is only considered "last" if:
            // - Not in return trip mode, OR
            // - In return trip mode with exactly 2 stops (both are real stops)
            const isLastStop = idx === reorderedStops.length - 1 && 
                             (!route.returnEnabled || reorderedStops.length === 2);
            
            if (isFirst) {
                // First stop should always be pickup
                stop.charterStopType = 'pickup';
            } else if (isLastStop) {
                // Last stop should always be dropoff
                stop.charterStopType = 'dropoff';
            }
            // For middle stops, keep their existing charterStopType unchanged
        });
        
        // SYNCHRONOUSLY UPDATE LOCAL STATE: This eliminates flicker!
        // The UI instantly reflects the new order without waiting for parent state update
        setActiveStops(reorderedStops);

        // --- Everything below updates the parent/server state ---
        
        // RETURN TRIP SYNC: Keep last stop matching first stop
        // Only sync if there are more than 2 stops (in which case last is a return stop)
        // With exactly 2 stops, both are real stops and should keep their types (first=pickup, last=dropoff)
        if (route.returnEnabled && reorderedStops.length > 2) {
            const lastIndex = reorderedStops.length - 1;
            const firstStop = reorderedStops[0];
            const originalReturnStop = reorderedStops[lastIndex];
            
            // Copy first stop's properties to last stop while preserving return metadata
            reorderedStops[lastIndex] = {
                ...firstStop,
                stopTimeId: originalReturnStop.stopTimeId,
                arriveSecs: originalReturnStop.arriveSecs,
                departSecs: originalReturnStop.departSecs,
                charterStopType: 'dropoff',
                isReturnStop: true,
                immutable: true
            };
        }

        // CLONE ROUTE: Never mutate state directly (React best practice)
        const updatedRoute = route.clone();
        
        // UPDATE ALL ARRAYS: Route stores stops in multiple places - update them all
        // This is required because route.stops, route.stopTimes, and trip stops all
        // need to reference the same reordered array
        updatedRoute.stops = reorderedStops;
        updatedRoute.stopTimes = reorderedStops;
        updatedRoute.services.forEach(service => {
            service.stops = reorderedStops;
            service.stopTimes = reorderedStops;
        });
        
        // IMMEDIATE STATE UPDATE: User sees reorder instantly (Priority 2: Performance)
        setRouteNow(updatedRoute);
        setUpdated(true);

        // STEP 2: BACKGROUND ROUTE RECALCULATION
        // Only recalculate if we have real stops with locations
        // Placeholder stops (empty) don't need route calculation
        const realStops = reorderedStops.filter(s => s.stopId && !s.placeholder);
        
        // Determine if recalculation is needed
        const isPlaceholder = draggedStop.placeholder;
        const movedToFirstPosition = newIndex === 0;
        const movedFromFirstPosition = oldIndex === 0;
        
        // Critical exception: placeholder moved to first position in return mode needs recalc
        const needsRecalc = !isPlaceholder || (isPlaceholder && movedToFirstPosition && route.returnEnabled);
        
        if (realStops.length < 2 || !needsRecalc) {
            // Skip recalculation if:
            // - Not enough real stops (need at least 2)
            // - Placeholder was dragged (not to first position in return mode)
            return;
        }
        
        // Show loading state so user knows calculation is happening
        setIsRecalculating(true);
        
        // ASYNC RECALCULATION: Runs in background without blocking UI
        const recalculateRoute = async () => {
            try {
                // Update mirror if first position changed in return mode
                if (route.returnEnabled && (movedToFirstPosition || movedFromFirstPosition)) {
                    const mirror = getMirrorStop(updatedRoute.stops);
                    const firstStop = updatedRoute.stops[0];
                    
                    if (mirror) {
                        if (!firstStop.placeholder) {
                            // Update mirror to match new first stop
                            mirror.lat = firstStop.lat;
                            mirror.lon = firstStop.lon;
                            mirror.stopId = firstStop.stopId;
                            mirror.stopName = firstStop.stopName;
                            mirror.address = firstStop.address;
                            delete mirror.placeholder;
                            // CRITICAL: Preserve return stop flags
                            mirror.isReturnStop = true;
                            mirror.immutable = true;
                        } else {
                            // Mark mirror as pending
                            mirror.placeholder = true;
                            mirror.lat = null;
                            mirror.lon = null;
                            mirror.stopId = null;
                            mirror.stopName = '';
                            // CRITICAL: Preserve return stop flags even when placeholder
                            mirror.isReturnStop = true;
                            mirror.immutable = true;
                        }
                    }
                }
                
                // Preserve first stop's original times
                const firstStop = reorderedStops[0];
                const defaultArriveSecs = firstStop.arriveSecs || 0;
                const defaultDepartSecs = firstStop.departSecs || 0;
                
                // REBUILD WAYPOINTS: This recalculates the route path between stops
                // This is the expensive operation but necessary for correct route
                // (Priority 2: Performance - runs in background so UI stays responsive)
                await updatedRoute.rebuildWaypoints({
                    arriveSecs: defaultArriveSecs,
                    departSecs: defaultDepartSecs
                });
                
                // PRESERVE MIRROR STOP FLAGS BEFORE calculateStartEnd
                // rebuildWaypoints may have lost the flags, so restore them now
                if (route.returnEnabled && updatedRoute.stops.length > 0) {
                    const lastStop = updatedRoute.stops[updatedRoute.stops.length - 1];
                    const firstStop = updatedRoute.stops[0];
                    
                    // If last stop has same stopId as first stop, it's the mirror stop
                    if (lastStop && firstStop && lastStop.stopId === firstStop.stopId) {
                        lastStop.isReturnStop = true;
                        lastStop.immutable = true;
                    }
                }
                
                // RECALCULATE TIMES: Update arrival/departure times for all stops
                updatedRoute.calculateStartEnd({
                    stops: updatedRoute.stops,
                    firstStop: updatedRoute.stops[0],
                    lastStop: updatedRoute.stops[updatedRoute.stops.length - 1],
                    trim: true
                });

                // PRESERVE MIRROR STOP FLAGS AFTER calculateStartEnd
                // calculateStartEnd or trim may have cleared the flags, so restore them again
                if (route.returnEnabled && updatedRoute.stops.length > 0) {
                    const lastStop = updatedRoute.stops[updatedRoute.stops.length - 1];
                    const firstStop = updatedRoute.stops[0];
                    
                    // If last stop has same stopId as first stop, it's the mirror stop
                    if (lastStop && firstStop && lastStop.stopId === firstStop.stopId) {
                        lastStop.isReturnStop = true;
                        lastStop.immutable = true;
                    }
                }

                if (fitMapToRoute) {
                    fitMapToRoute(updatedRoute.stops, { force: true });
                }
                
                // Update state with fully recalculated route
                setRouteNow(updatedRoute.clone());
                setUpdated(true);
                
            } catch (error) {
                // ERROR HANDLING: Don't crash the app if recalculation fails
                // User already sees the reordered stops, just without updated route
                console.error('[SimpleStopTimes] Error recalculating route after drag:', error);
                
            } finally {
                // CLEANUP: Always clear loading state, even if error occurred
                setIsRecalculating(false);
            }
        };
        
        // Start the background calculation
        recalculateRoute();
        
    }, [route, activeStops, setRouteNow, setUpdated, messageApi, fitMapToRoute]);

    const handleStartDepotChange = async (stopId) => {
        const depot = allDepots[stopId];
        let startDepotDistance = 0;
        let startDepotDuration = 0;
        if(depot && trip.stops?.[0]?.stopId) {
          const {distance, duration} = await getCachedPathDebounced({profile: 'bus', startCoord: depot, endCoord: trip.stops[0]}); 
          startDepotDistance = distance;
          startDepotDuration = duration;
        }
        const _clone = route.clone();
        _clone.startDepot = depot;
        _clone.startDepotDistance = startDepotDistance;
        _clone.startDepotDuration = startDepotDuration;
        setRouteNow(_clone);
    };

    const handleEndDepotChange = async (stopId) => {
        const depot = allDepots[stopId];
        let endDepotDistance = 0;
        let endDepotDuration = 0;
        if(depot && last(trip.stops)?.stopId) {
          const {distance, duration} = await getCachedPathDebounced({profile: 'bus', startCoord: last(trip.stops), endCoord: depot}); 
          endDepotDistance = distance;
          endDepotDuration = duration;
        }
        const _clone = route.clone();
        _clone.endDepot = depot;
        _clone.endDepotDistance = endDepotDistance;
        _clone.endDepotDuration = endDepotDuration;
        setRouteNow(_clone);
    };

    const handleReturnChange = async (enabled) => {
        if (enabled === route.returnEnabled) return;

        const _clone = route.clone();
        _clone.returnEnabled = enabled;

        const firstStop = _clone.stops[0];
        const lastStop = _clone.stops[_clone.stops.length - 1];

        if (enabled) {
            // ENABLING return mode

            // Remove existing mirror if present (safety check)
            if (lastStop?.isReturnStop) {
                // Use manual removal instead of buggy removeStop method
                const removeIdx = _clone.stops.length - 1;
                const removeStopAtIndex = (arr, index) => arr.filter((_, i) => i !== index);
                _clone.stops = removeStopAtIndex(_clone.stops, removeIdx);
                _clone.stopTimes = removeStopAtIndex(_clone.stopTimes, removeIdx);
                _clone.services.forEach(service => {
                    if (service.stops) {
                        service.stops = removeStopAtIndex(service.stops, removeIdx);
                    }
                    if (service.stopTimes) {
                        service.stopTimes = removeStopAtIndex(service.stopTimes, removeIdx);
                    }
                });
            }

            if (firstStop?.stopId && !firstStop.placeholder) {
                // First stop is real: create mirror with editable timings
                const mirrorStop = {
                    ...firstStop,
                    stopTimeId: ulid(),
                    isReturnStop: true,
                    immutable: true,
                    charterStopType: 'dropoff', // Mirror is always dropoff
                    arriveSecs: 0, // Keep timing fields editable
                    departSecs: 0
                };
                delete mirrorStop.placeholder;

                await _clone.appendStop(mirrorStop, null, true);
            } else {
                // First stop is placeholder: append pending mirror (still with timing structure)
                const pendingMirror = {
                    stopTimeId: ulid(),
                    isReturnStop: true,
                    immutable: true,
                    placeholder: true,
                    stopName: '',
                    stopId: null,
                    lat: null,
                    lon: null,
                    charterStopType: 'dropoff',
                    arriveSecs: 0,
                    departSecs: 0
                };

                _clone.stops.push(pendingMirror);
                _clone.stopTimes.push(pendingMirror);
            }

        } else {
            // DISABLING return mode

            // Find return stop (should be last, but verify)
            const returnStopIdx = _clone.stops.findIndex(s => s.isReturnStop);
            
            if (returnStopIdx !== -1) {
                // Use manual removal instead of buggy removeStop method
                const removeStopAtIndex = (arr, index) => arr.filter((_, i) => i !== index);
                _clone.stops = removeStopAtIndex(_clone.stops, returnStopIdx);
                _clone.stopTimes = removeStopAtIndex(_clone.stopTimes, returnStopIdx);
                _clone.services.forEach(service => {
                    if (service.stops) {
                        service.stops = removeStopAtIndex(service.stops, returnStopIdx);
                    }
                    if (service.stopTimes) {
                        service.stopTimes = removeStopAtIndex(service.stopTimes, returnStopIdx);
                    }
                });
            } else {
                console.warn('[SimpleStopTimes] handleReturnChange DISABLING - no return stop found!', {
                    allStops: _clone.stops.map(s => ({
                        name: s.stopName || s.stopId,
                        isReturn: !!s.isReturnStop
                    }))
                });
            }

            // Update last real stop to be 'dropoff' type
            if (_clone.stops.length > 0) {
                const newLastStop = _clone.stops[_clone.stops.length - 1];
                if (newLastStop.charterStopType) {
                    newLastStop.charterStopType = 'dropoff';
                }
            }
        }

        _clone.calculateStartEnd({
            firstStop: _clone.stops[0],
            lastStop: _clone.stops[_clone.stops.length - 1],
            trim: true
        });

        setRouteNow(_clone);
    };

    const handleVehicleTypeChange = (selectedVehicleTypeIds) => {
        const updatedTrip = trip.clone();
        if (!updatedTrip.vehicleTypeQuantities) {
            updatedTrip.vehicleTypeQuantities = {};
        }
        const currentQuantities = { ...updatedTrip.vehicleTypeQuantities };
        Object.keys(currentQuantities).forEach(id => {
            if (!selectedVehicleTypeIds.includes(id)) {
                delete currentQuantities[id];
            }
        });

        selectedVehicleTypeIds.forEach(id => {
            if (!currentQuantities[id]) {
                currentQuantities[id] = 1; // Default to 1 vehicle
            }
        });

        updatedTrip.vehicleTypeQuantities = currentQuantities;
        updatedTrip.vehicleTypes = selectedVehicleTypeIds;
        setTrip(updatedTrip, true);
    };

    const handleVehicleQuantityChange = (vehicleTypeId, quantity) => {
        const updatedTrip = trip.clone();
        if (!updatedTrip.vehicleTypeQuantities) {
            updatedTrip.vehicleTypeQuantities = {};
        }
        updatedTrip.vehicleTypeQuantities[vehicleTypeId] = parseInt(quantity) || 0;

        setTrip(updatedTrip, true);
    };

    const returnEnabled = route.returnEnabled;

    return allStops ? (
        <div className="SimpleStopTimes h-100">
            <div className="icon-info-text">
                <div className="w-100">
                    <div className="d-flex align-items-center pt-1">
                        <div className="d-flex flex-column" style={{ width: '50%', gap: '4px' }}>
                            <label style={{fontSize: '13px', color: '#838792'}}>Select vehicle type</label>
                            <Select
                                mode="multiple"
                                style={{ width: '100%' }}
                                placeholder="Select vehicle types"
                                value={trip.vehicleTypes || []}
                                onChange={handleVehicleTypeChange}
                                options={allVehicleTypesArray?.map(vehicleType => ({
                                value: vehicleType.vehicleTypeId,
                                label: vehicleType.vehicleTypeName
                                }))}
                                status={!trip.vehicleTypes || trip.vehicleTypes?.length === 0 ? "error" : ""}
                            />
                        </div>
                        {trip.vehicleTypes && trip.vehicleTypes.length > 0 && (
                            <div className="d-flex flex-wrap" style={{ width: '50%', gap: '8px', paddingLeft: '14px' }}>
                            {trip.vehicleTypes.map(id => (
                                <div key={id} style={{ display: 'flex', flexDirection: 'column' }}>
                                    <span style={{ marginRight: '4px', fontSize: '12px' }}>
                                        {allVehicleTypes?.[id]?.vehicleTypeName}:
                                    </span>
                                    <Input
                                        type="number"
                                        min={1}
                                        style={{ width: '42px', padding: '4px 0 4px 8px' }}
                                        value={trip.vehicleTypeQuantities?.[id] || 1}
                                        onChange={(e) => handleVehicleQuantityChange(id, e.target.value)}
                                    />
                                </div>
                            ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <Row gutter={16}>
                <Col span={24} className="pt-2 pb-3">
                    <Form.Item label="Start Depot">
                        <Select
                            placeholder="Select start depot"
                            value={route.startDepot?.stopId}
                            onChange={handleStartDepotChange}
                            options={Object.values(allDepots || {}).map(depot => ({
                                value: depot.stopId,
                                label: depot.stopName
                            }))}
                            showSearch
                            filterOption={(input, option) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            style={{ height: '100%' }}
                            allowClear
                        />
                        {route.startDepot && charter && route.startDepotDuration > 0 && (
                            <div className="depot-time-info" style={{ fontSize: '13px', color: '#666', marginTop: '5px' }}>
                                <span style={{ fontWeight: 'bold' }}>Departure:</span> {formatDateTime(getStartDepotDepartureTime(route, trip.stops[0])) || 'Calculating...'}
                            </div>
                        )}
                    </Form.Item>
                </Col>
            </Row>
            {/* <div className='charter-type-button-wrap' style={{ marginBottom: 10 }}>
                <div className={`charter-type-button ${!returnEnabled ? 'active' : ''}`} onClick={() => handleReturnChange(false)}>
                    <Oneway style={{ height: 34 }} />
                    <strong>One Way</strong>
                </div>
                <div className={`charter-type-button ${returnEnabled ? 'active' : ''}`} onClick={() => handleReturnChange(true)}>
                    <Return style={{ height: 30 }} />
                    <strong>Return Trip</strong>
                </div>
            </div> */}
            <div className="service-rows-wrap stops-container">
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext items={activeStops.map(stop => getStableStopId(stop))} strategy={verticalListSortingStrategy}>
                        {
                            activeStops?.map((stopTime, idx) => {
                                const stopValidationMessages = validationHelper.stops[idx] ? validationHelper.stops[idx].filter(s => !!s && (!s.tripId || (s.stop && s.tripId === trip.tripId))) : []
                                const arriveValidationMessages = validationHelper.stops[idx] ? validationHelper.stops[idx].filter(s => !!s && s.tripId === trip.tripId && s.arrive) : []
                                const departValidationMessages = validationHelper.stops[idx] ? validationHelper.stops[idx].filter(s => !!s && s.tripId === trip.tripId && s.depart) : []
                                return (
                                    <StopTime
                                        route={route} charter={charter}
                                        key={getStableStopId(stopTime)} idx={idx} showSeconds={showSeconds} isAdmin={isAdmin}
                                        immutable={immutable} focus={focus} setFocus={setFocus}
                                        setEditing={setEditing}
                                        editing={editing} stopValidationMessages={stopValidationMessages}
                                        arriveValidationMessages={arriveValidationMessages}
                                        departValidationMessages={departValidationMessages}
                                        setFocusStop={setFocusStop} setSelectedStop={setSelectedStop}
                                        selectedStop={selectedStop}
                                        scrollRef={scrollRef} executeScroll={executeScroll} setUpdated={setUpdated} stop={stopTime}
                                        previousStop={activeStops[idx - 1]} previousStopTime={trip.stopTimes[idx - 1]}
                                        trip={trip} cumulativeUpdate={cumulativeUpdate}
                                        allStops={allStops} operator={operator} stopTimes={activeStops}
                                        setFocusTransfers={setFocusTransfers} focusTransfers={focusTransfers}
                                        setAllStops={setAllStops} handleAddStopTime={handleAddStopTime}
                                        fitMapToRoute={fitMapToRoute}
                                        setRoute={setRoute} setRouteNow={setRouteNow}
                                        calculateArrivalTime={calculateArrivalTime}
                                        viewOnly={route.returnEnabled && idx === activeStops.length - 1 && activeStops.filter(s=> s.stopId).length > 1}
                                        isRecalculating={isRecalculating}
                                    />
                                )
                            })
                        }
                    </SortableContext>
                </DndContext>
                {activeStops.length === 0 && (
                    <div className='button-separator'>
                        <Button type="primary" className="icon-button" icon={<PlusOutlined />} onClick={handleAddStopTime}>Add Stop</Button>
                    </div>)}
                <Row gutter={16} className='mt-3' style={{paddingBottom: '300px'}}>
                    <Col span={24}>
                        <Form.Item label="End Depot">
                            <Select
                                placeholder="Select end depot"
                                value={route.endDepot?.stopId}
                                onChange={handleEndDepotChange}
                                showSearch
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                options={Object.values(allDepots || {}).map(depot => ({
                                    value: depot.stopId,
                                    label: depot.stopName
                                }))}
                                style={{ height: '100%' }}
                                allowClear
                            />
                            {route.endDepot && charter && route.endDepotDuration > 0 && (
                                <div className="depot-time-info" style={{ fontSize: '13px', color: '#666', marginTop: '5px' }}>
                                    <span style={{ fontWeight: 'bold' }}>Arrival:</span> {formatDateTime(getEndDepotArrivalTime(route, last(trip.stops))) || 'Calculating...'}
                                </div>
                            )}
                        </Form.Item>
                    </Col>
                </Row>
            </div>
        </div>
    ) : <LoadMessage message={"Loading stops..."} />;
}
